import { remarkCitationNowrap } from "#features/citations/remarkCitationNowrap.ts";
import { Div, Link, Table } from "@hopper-ui/components";
import type { ReactNode, Ref } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGemoji from "remark-gemoji";
import remarkGfm from "remark-gfm";
import "./Markdown.css";
import "./Markdown.dark.css";

const REMARK_PLUGINS = [remarkGemoji, remarkGfm, remarkCitationNowrap];

interface MarkdownLinkProps {
    href?: string;
    children?: ReactNode;
}

// Default renderer for Markdown links. Exported so citation renderers reuse the exact same link
// styling for their non-citation fallback, keeping the two in sync.
export const MarkdownLink = ({ href, children }: MarkdownLinkProps) => {
    return (
        <Link
            size="sm"
            variant="secondary"
            target="_blank"
            href={href}
        >
            {children}
        </Link>
    );
};

const defaultComponents: Components = {
    a: MarkdownLink,
    table({ children }) {
        return (
            <Div className="wai-markdown-table">
                <Table>
                    {children}
                </Table>
            </Div>
        );
    }
};

export interface MarkdownProps {
    /** The Markdown text to render. */
    content: string;
    /**
     * Overrides for the elements produced by the Markdown parser, merged over the built-in
     * anchor and table renderers. Used to inject custom rendering such as citation chips.
     */
    components?: Components;
    /** Ref forwarded to the wrapper element. */
    contentRef?: Ref<HTMLDivElement>;
}

/** Renders Markdown with Workleap AI typography, GitHub-flavored Markdown, and emoji shortcodes. */
export const Markdown = ({ content, components, contentRef }: MarkdownProps) => {
    return (
        <Div className="wai-markdown" ref={contentRef}>
            <ReactMarkdown
                remarkPlugins={REMARK_PLUGINS}
                components={{ ...defaultComponents, ...components }}
            >
                {content}
            </ReactMarkdown>
        </Div>
    );
};
